import { Box, Chip, Container, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { useToaster } from 'src/utils/toaster/toasterContext';
import GetStartedBrandStep from './getStartedBrandStep';
import GetStartedOrgStep from './getStartedOrgStep';
import GetStartedPaymentStep from './getStartedPaymentStep';
import { emptyChecklist } from './getStartedShared';
import GetStartedTopBar from './getStartedTopBar';

export default function GetStartedComponent() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToaster();
  const [loader, setLoader] = useState(true);
  const [checklist, setChecklist] = useState(emptyChecklist);
  const [activeStep, setActiveStep] = useState('org');
  const [orgForm, setOrgForm] = useState({ org_name: '', org_description: '' });
  const [brandForm, setBrandForm] = useState({
    brand_name: '',
    description: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });
  const [savingStep, setSavingStep] = useState('');

  const orgComplete = Boolean(checklist.org_done || checklist.org_skipped);
  const brandComplete = Boolean(checklist.brand_done || checklist.brand_skipped);
  const paymentComplete = Boolean(checklist.payment_done || checklist.payment_skipped);
  const allComplete = orgComplete && brandComplete && paymentComplete;
  const isPlanOverMode = searchParams.get('mode') === 'plan-over';

  const syncLocalChecklist = useCallback((nextChecklist) => {
    const normalized = { ...emptyChecklist, ...(nextChecklist || {}) };
    LocalStorage.setItem('get_started', JSON.stringify(normalized));
    setChecklist(normalized);
    return normalized;
  }, []);

  const loadChecklist = useCallback(() => {
    setLoader(true);
    HttpServices.Get({
      url: userModuleURL.getStartedChecklist,
      successCallback: (data) => {
        const normalized = syncLocalChecklist(data);
        if (searchParams.get('step') === 'payment' || isPlanOverMode) setActiveStep('payment');
        else if (!(normalized.org_done || normalized.org_skipped)) setActiveStep('org');
        else if (!(normalized.brand_done || normalized.brand_skipped)) setActiveStep('brand');
        else if (!(normalized.payment_done || normalized.payment_skipped)) setActiveStep('payment');
        setLoader(false);
      },
      failureCallback: (message) => {
        setLoader(false);
        showToast(
          message ||
            translate('get_started_page.errors.load_checklist', { defaultValue: 'Unable to load setup checklist' }),
          'e'
        );
      },
    });
  }, [isPlanOverMode, searchParams, showToast, syncLocalChecklist, translate]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  useEffect(() => {
    if (allComplete && !isPlanOverMode) {
      navigate(CommonHelper.GetDefaultPermissionRoute(), { replace: true });
    }
  }, [allComplete, isPlanOverMode, navigate]);

  const markStep = (step, status, successMessage) =>
    new Promise((resolve, reject) => {
      HttpServices.Post({
        url: userModuleURL.updateGetStartedChecklist,
        body: { step, status },
        successCallback: (data) => {
          const normalized = syncLocalChecklist(data);
          successMessage && showToast(successMessage, 's');
          resolve(normalized);
        },
        failureCallback: (message) => {
          const fallbackMessage = translate('get_started_page.errors.update_checklist', {
            defaultValue: 'Unable to update setup checklist',
          });
          showToast(message || fallbackMessage, 'e');
          reject(new Error(message || fallbackMessage));
        },
      });
    });

  const saveOrg = () => {
    if (!orgForm.org_name.trim()) {
      showToast(
        translate('get_started_page.errors.organization_name_required', {
          defaultValue: 'Organization name is required',
        }),
        'e'
      );
      return;
    }
    setSavingStep('org');
    HttpServices.Post({
      url: userModuleURL.updateOrg,
      body: {
        org_name: orgForm.org_name,
        org_description: orgForm.org_description,
      },
      successCallback: () => {
        markStep(
          'org',
          'done',
          translate('get_started_page.toasts.organization_completed', {
            defaultValue: 'Organization setup completed',
          })
        )
          .then(() => setActiveStep('brand'))
          .finally(() => setSavingStep(''));
      },
      failureCallback: (message) => {
        setSavingStep('');
        showToast(
          message ||
            translate('get_started_page.errors.save_organization', { defaultValue: 'Unable to save organization' }),
          'e'
        );
      },
    });
  };

  const saveBrand = () => {
    if (!brandForm.brand_name.trim()) {
      showToast(
        translate('get_started_page.errors.brand_name_required', { defaultValue: 'Brand name is required' }),
        'e'
      );
      return;
    }
    setSavingStep('brand');
    HttpServices.Post({
      url: userModuleURL.updateBrand,
      body: {
        brand_name: brandForm.brand_name,
        description: brandForm.description,
        timezone: brandForm.timezone,
      },
      successCallback: () => {
        markStep(
          'brand',
          'done',
          translate('get_started_page.toasts.brand_completed', { defaultValue: 'Brand setup completed' })
        )
          .then(() => setActiveStep('payment'))
          .finally(() => setSavingStep(''));
      },
      failureCallback: (message) => {
        setSavingStep('');
        showToast(
          message || translate('get_started_page.errors.save_brand', { defaultValue: 'Unable to save brand' }),
          'e'
        );
      },
    });
  };

  const skipStep = (step) => {
    setSavingStep(step);
    markStep(
      step,
      'skipped',
      step === 'org'
        ? translate('get_started_page.toasts.organization_skipped', { defaultValue: 'Organization setup skipped' })
        : translate('get_started_page.toasts.brand_skipped', { defaultValue: 'Brand setup skipped' })
    )
      .then((nextChecklist) => {
        if (step === 'org') setActiveStep('brand');
        else if (step === 'brand' && !(nextChecklist.payment_done || nextChecklist.payment_skipped)) {
          setActiveStep('payment');
        }
      })
      .finally(() => setSavingStep(''));
  };

  const skipPayment = () => {
    setSavingStep('payment');
    HttpServices.Post({
      url: userModuleURL.skipPaymentAddTrailOnboarding,
      successCallback: () => {
        markStep(
          'payment',
          'skipped',
          translate('get_started_page.toasts.trial_active', { defaultValue: 'Trial plan is active' })
        )
          .then(() => window.location.reload())
          .finally(() => setSavingStep(''));
      },
      failureCallback: (message) => {
        setSavingStep('');
        showToast(
          message ||
            translate('get_started_page.errors.activate_trial', { defaultValue: 'Unable to activate trial plan' }),
          'e'
        );
      },
    });
  };

  const steps = useMemo(
    () => [
      {
        key: 'org',
        label: translate('get_started_page.steps.create_organization', { defaultValue: 'Create organization' }),
        done: orgComplete,
      },
      {
        key: 'brand',
        label: translate('get_started_page.steps.create_brand', { defaultValue: 'Create brand' }),
        done: brandComplete,
      },
      {
        key: 'payment',
        label: translate('get_started_page.steps.payment', { defaultValue: 'Payment' }),
        done: paymentComplete,
      },
    ],
    [brandComplete, orgComplete, paymentComplete, translate]
  );

  if (loader) return <Spinner />;

  return (
    <Box
      sx={{
        height: '100vh',
        bgcolor: '#f6f7fb',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <GetStartedTopBar />
      <Box sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minHeight: 'calc(100vh - 86px)' }}>
        <Container maxWidth="lg">
          <Stack spacing={2.5}>
            {!isPlanOverMode && (
              <Box
                sx={{
                  p: { xs: 2, md: 2.25 },
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Box sx={{ maxWidth: 420 }}>
                    <Typography variant="h4" fontWeight={900}>
                      {translate('get_started_page.title', { defaultValue: 'Get started' })}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      {translate('get_started_page.subtitle', {
                        defaultValue: 'Complete these setup steps to enter your workspace.',
                      })}
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ flex: 1, maxWidth: { md: 720 } }}>
                    {steps.map((step, index) => (
                      <Chip
                        key={step.key}
                        color={step.done ? 'success' : activeStep === step.key ? 'primary' : 'default'}
                        label={`${index + 1}. ${step.label}`}
                        onClick={() => {
                          if (step.key === 'brand' && !orgComplete) return;
                          if (step.key === 'payment' && !isPlanOverMode && (!orgComplete || !brandComplete)) return;
                          setActiveStep(step.key);
                        }}
                        sx={{
                          height: 42,
                          flex: 1,
                          justifyContent: 'flex-start',
                          fontWeight: 800,
                          borderRadius: 1.5,
                          '& .MuiChip-label': { width: 1 },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Box>
            )}

            <Box sx={{ width: '100%', mx: 'auto' }}>
              {activeStep === 'org' && (
                <GetStartedOrgStep
                  orgForm={orgForm}
                  setOrgForm={setOrgForm}
                  orgComplete={orgComplete}
                  savingStep={savingStep}
                  onSave={saveOrg}
                  onSkip={() => skipStep('org')}
                />
              )}

              {activeStep === 'brand' && (
                <GetStartedBrandStep
                  brandForm={brandForm}
                  setBrandForm={setBrandForm}
                  orgComplete={orgComplete}
                  brandComplete={brandComplete}
                  savingStep={savingStep}
                  onSave={saveBrand}
                  onSkip={() => skipStep('brand')}
                />
              )}

              {activeStep === 'payment' && (
                <GetStartedPaymentStep
                  paymentComplete={paymentComplete}
                  savingStep={savingStep}
                  isPlanOverMode={isPlanOverMode}
                  onSkip={skipPayment}
                />
              )}
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
